/**
 * Long-form prose for the about page. `**bold**` markers are rendered by
 * Components/RichText.jsx, which keeps this file free of JSX.
 *
 * Two rules for this file, both learned the hard way.
 *
 * 1. At most one emphasis per paragraph. The previous version bolded roughly
 *    every third phrase, which cancels itself out: when everything is
 *    emphasised the reader sees an evenly grey page with no signal in it, and
 *    it reads like a keyword-stuffed CV rather than a person writing.
 *
 * 2. Do not re-tell the competitions here. The Achievements section below
 *    carries them with full context, and the long version of this file told
 *    each of them twice on the same page. Any rank mentioned in passing must
 *    match data/achievements.js exactly; the prose and the bullets drifted
 *    apart once already.
 */

export const headline = "Backend Engineer → AI Systems";

// AI first, matching the order the rest of the site argues for. These sit
// directly under the headline, so they should not simply restate it.
export const roleChips = ["AI Engineering", "Machine Learning", "Backend Systems"];

export const professionalSummary = [
  // --- The thesis ----------------------------------------------------------
  "I build **backend systems for fintech**, and I build the AI layer that runs on top of them. Those are not two separate careers. The reason an LLM application survives contact with real users is usually the same reason any service does: the data model holds, the integrations are tested, and somebody thought about what happens under load.",

  // --- Where it came from --------------------------------------------------
  "Most of what I know came out of **FOO**. I started there as a backend intern, built a K6 and Grafana load testing framework that located the bottlenecks behind a 29% cut in response latency, supported the migration of product microservices from MySQL to PostgreSQL, and raised unit test coverage by 31%. Three months in, I was brought on full-time.",

  "As a full-time engineer I led the backend of the **Mifos core banking integration** into the Yeepal product, built as Java and Spring Boot microservices exposed through WSO2. I took it from the first requirement through UAT to client-facing rollout, alongside two product owners, a tech lead and two QA engineers. Shipping into a system where a defect has somebody's money attached to it changes how carefully you work, permanently.",

  "Before FOO, at **IBC Solutions**, I worked on Finastra's Fusion Corporate Channels, a corporate digital banking platform used by banks in Saudi Arabia, the UAE, Bahrain and Qatar. I built Spring Boot REST APIs and Angular modules, and spent a good share of my time sitting with client banking teams turning what they asked for into something that could actually be built.",

  // --- The AI turn ---------------------------------------------------------
  "The move into AI is deliberate rather than a pivot away from any of that. I am taking **AUB's Graduate Diploma in AI and Data Science** alongside full-time work, I hold Meta's Generative AI for Developers certificate, and I build with LLM APIs, retrieval and agent frameworks in my own projects. My senior capstone had already put YOLOv8 and LBPH into a working attendance system, which is where I learned that the model is the easy part and the system around it is the actual job.",

  // --- How I work ----------------------------------------------------------
  "I have been **teaching since 2024**, as a teaching assistant at Rafik Hariri University, as a tutor at GeekExpress, and privately with students from AUB, LAU, NDU and LU. Explaining pointers to someone for the third time, in a third different way, is the fastest method I know for finding out whether you understand them yourself.",

  "What I am after is not complicated: build things that work, and carry enough backend discipline to keep them working once people depend on them.",
];

/**
 * The rail beside the professional summary.
 *
 * The split layout holds the prose to a readable measure and lets the heading
 * column absorb the rest of the row. With the summary cut to 440 words that
 * column became a tall empty gutter, so it now carries the facts a visitor
 * would otherwise have to read seven paragraphs to assemble.
 */
export const quickFacts = [
  { label: "Based in", value: "Beirut, Lebanon" },
  { label: "Currently", value: "Machine Learning Trainee at Pathline Training Academy" },
  { label: "Building", value: "LLM applications, RAG systems, AI agents" },
  { label: "Studying", value: "Graduate Diploma in AI and Data Science, AUB" },
];

/**
 * Short version used in the home page "Background" card. Kept separate from the
 * summary above so the home page can stay brief without the two going stale.
 */
export const backgroundSummary =
  "I started at FOO as a backend intern, building a K6 and Grafana load testing framework that cut response latency by 29%, supporting a MySQL to PostgreSQL migration, and raising unit test coverage by 31%. Three months later I was full-time, leading the backend of the Mifos core banking integration into Yeepal through WSO2. The AI work builds on that foundation rather than replacing it: LLM applications, retrieval and agents, with a production backend underneath.";
